import React, {useMemo} from 'react'
import useMediaQuery from '@mui/material/useMediaQuery'
import {createTheme, Theme, ThemeProvider} from '@mui/material/styles'
import {LocalizationProvider} from '@mui/x-date-pickers'
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs'
import CssBaseline from '@mui/material/CssBaseline'
import Routes from './routes'
import '@mui/material/styles'
import {CachedObjectsProvider} from './hooks/useCachedObjects'
import {UserProvider} from './hooks/useUser'

const makePrimaryPalette = (theme: Theme, dark?: boolean) => {
  if (!dark) return theme.palette.primary

  return theme.palette.augmentColor({
    color: {
      light: '#1D2472',
      main: 'rgb(127, 189, 248)',
      dark: '#ADB3EB',
    },
    name: 'primary',
  })
}

const secondaryPalette = (theme: Theme, dark?: boolean) =>
  dark
    ? {
        ...theme.palette.augmentColor({
          color: {
            main: '#3351FF',
          },
          name: 'secondary',
        }),
      }
    : {
        ...theme.palette.augmentColor({
          color: {
            main: '#F1F3FF',
          },
          name: 'secondary',
        }),
        dark: 'rgb(159, 189, 255)',
      }

function App() {
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)')

  const theme = useMemo(() => {
    const theme = createTheme({
      typography: {
        fontFamily: 'Montserrat, Arial',
      },
      palette: {mode: prefersDarkMode ? 'dark' : 'light'},
    })

    return createTheme(theme, {
      palette: {
        primary: makePrimaryPalette(theme, prefersDarkMode),
        secondary: secondaryPalette(theme, prefersDarkMode),
      },
    })
  }, [prefersDarkMode])

  return (
    <CachedObjectsProvider>
      <UserProvider>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Routes />
          </LocalizationProvider>
        </ThemeProvider>
      </UserProvider>
    </CachedObjectsProvider>
  )
}

export default App
